import { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CalendarIcon, MapPin, Check, Luggage, Lightbulb, Weight, ChevronRight, PartyPopper, Search, Info, History, Plus, ChevronDown, Lock, Plane } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { PackingList } from './PackingList';
import { PackingTips } from './PackingTips';
import { PreTripLaundry } from './PreTripLaundry';
import { WeightEstimator } from './WeightEstimator';
import { PackingTimeEstimator } from './PackingTimeEstimator';
import { PackingLoadingAnimation } from './PackingLoadingAnimation';
import { LuggageCompletionAnimation } from './LuggageCompletionAnimation';
import { VisaCheckModal } from './VisaCheckModal';
import { MultiCityPackingWorkflow } from './MultiCityPackingWorkflow';
import { TemplateManager } from './TemplateManager';
import { OfflinePackingManager } from '@/components/features/OfflinePackingManager';
import { GroupPackingSharing } from '@/components/features/GroupPackingSharing';
import { ForgottenItemsReminder } from './ForgottenItemsReminder';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useAirlineData } from '@/hooks/useAirlineData';
import { useSubscription } from '@/hooks/useSubscription';
import { useFeatureUsage } from '@/hooks/useFeatureUsage';
import { LOCATIONS as DESTINATIONS } from '@/components/ui/location-autocomplete';
import { convertWeatherData, type TemperatureUnit } from '@/utils/temperatureUtils';

// Using shared comprehensive global destinations database

// Database-driven airline data - no longer using hardcoded data

interface PackingFormData {
  destination: string;
  destination_country: string;
  start_date?: Date;
  end_date?: Date;
  trip_type: string;
  travel_class: string;
  luggage_type: string[];
  weight_unit: 'kg' | 'lbs';
  carry_on_weight: string;
  checked_weight: string;
  airline: string;
  // Airline baggage policy info
  airline_baggage_policy?: {
    carryOn: { weight: string; dimensions: string };
    checked: { weight: string; dimensions: string };
    personal: { weight: string; dimensions: string };
  };
}

interface PackingWorkflowProps {
  onComplete: () => void;
}

export function PackingWorkflow({ onComplete }: PackingWorkflowProps) {
  const [currentStep, setCurrentStep] = useState<'form' | 'loading' | 'packing' | 'luggage-animation' | 'complete'>('form');
  const [formData, setFormData] = useState<PackingFormData>({
    destination: '',
    destination_country: '',
    start_date: undefined,
    end_date: undefined,
    trip_type: '',
    travel_class: '',
    luggage_type: [],
    weight_unit: 'kg',
    carry_on_weight: '',
    checked_weight: '',
    airline: ''
  });
  const [destinationInput, setDestinationInput] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [airlineInput, setAirlineInput] = useState('');
  const [showAirlineSuggestions, setShowAirlineSuggestions] = useState(false);
  const [weatherData, setWeatherData] = useState<any>(null);
  const [displayWeatherData, setDisplayWeatherData] = useState<any>(null);
  const [packingItems, setPackingItems] = useState<any[]>([]);
  const [userPreferences, setUserPreferences] = useState<any>(null);
  const [showVisaModal, setShowVisaModal] = useState(false);
  const [userTrips, setUserTrips] = useState<any[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string>('new');
  const [showPreviousListsModal, setShowPreviousListsModal] = useState(false);
  const [previousLists, setPreviousLists] = useState<any[]>([]);
  const [isTripSectionOpen, setIsTripSectionOpen] = useState(false);
  const [showMultiCityMode, setShowMultiCityMode] = useState(false);
  const [formStep, setFormStep] = useState<1 | 2 | 3 | 4>(1);
  const totalSteps = 4;
  const stepLabels = ['Destination', 'Dates', 'Trip Type', 'Luggage'] as const;
  const stepDescriptions = [
    'Where are you headed?',
    'When are you traveling?',
    'What kind of trip is it?',
    'What are you packing in?',
  ] as const;
  const { user } = useAuth();
  const { toast } = useToast();
  const { hasFeature, subscription } = useSubscription();
  const { canUseFeature, recordUsage, getRemainingUses } = useFeatureUsage();
  const isMultiCityLocked = !hasFeature('multi_city_packing');

  // Use the airline data hook for live database updates
  const { airlines, loading: airlinesLoading, error: airlinesError, getBaggagePolicy, getAirlineNames } = useAirlineData();

  // Fetch user's trips for linking and templates
  useEffect(() => {
    const fetchUserTrips = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('trips')
        .select('id, title, destination, start_date, end_date, packing_list')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false });

      if (!error && data) {
        setUserTrips(data);
        // Collect previous lists that have packing data
        const lists = data
          .filter(t => t.packing_list)
          .map(t => ({
            id: t.id,
            title: t.title,
            destination: t.destination,
            dates: `${format(new Date(t.start_date), 'MMM d')} - ${format(new Date(t.end_date), 'MMM d, yyyy')}`,
            packingList: t.packing_list
          }));
        setPreviousLists(lists);
      }
    };

    fetchUserTrips();
  }, [user]);

  // Use direct input value for immediate filtering
  const filteredDestinations = useMemo(() => {
    if (!destinationInput.trim()) return [];
    const searchTerm = destinationInput.toLowerCase().trim();
    console.log('Filtering destinations for:', searchTerm);
    
    const results = DESTINATIONS.filter(destination =>
      destination.name.toLowerCase().includes(searchTerm) ||
      destination.country.toLowerCase().includes(searchTerm)
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 8);
    
    console.log('Found destinations:', results.length, results.map(d => d.name));
    return results;
  }, [destinationInput]);

  const filteredAirlines = useMemo(() => {
    if (!airlineInput.trim() || airlinesLoading) return [];
    const airlineNames = getAirlineNames();
    return airlineNames.filter(airline =>
      airline.toLowerCase().includes(airlineInput.toLowerCase())
    ).slice(0, 6);
  }, [airlineInput, getAirlineNames, airlinesLoading]);

  const selectCity = useCallback((city: { name: string; country: string }) => {
    setFormData(prev => ({ 
      ...prev, 
      destination: city.name,
      destination_country: city.country 
    }));
    setDestinationInput(`${city.name}, ${city.country}`);
    setShowCitySuggestions(false);
  }, []);

  const handleDestinationChange = useCallback((value: string) => {
    console.log('Destination input changed:', value);
    setDestinationInput(value);
    
    // Show suggestions when typing, hide when empty
    const shouldShow = value.trim().length > 0;
    console.log('Should show suggestions:', shouldShow, 'for value:', value);
    setShowCitySuggestions(shouldShow);
    
    // Only clear form data when input is completely empty
    if (!value.trim()) {
      setFormData(prev => ({ ...prev, destination: '', destination_country: '' }));
    }
  }, []);

  const handleDestinationFocus = useCallback(() => {
    // Show suggestions if there's content when focusing
    if (destinationInput.trim().length > 0) {
      setShowCitySuggestions(true);
    }
  }, [destinationInput]);

  // Helper function to extract weight numbers from policy text
  const extractWeight = useCallback((weightText: string): string => {
    if (!weightText || weightText === 'No limit' || weightText === 'N/A') return '';
    const match = weightText.match(/(\d+(?:\.\d+)?)/);
    return match ? match[1] : '';
  }, []);

  const selectAirline = useCallback((airline: string) => {
    setFormData(prev => {
      const baggagePolicy = getBaggagePolicy(airline, prev.travel_class || 'economy');
      
      let carryOnWeight = prev.carry_on_weight;
      let checkedWeight = prev.checked_weight;

      // Auto-fill weight limits if baggage policy exists
      if (baggagePolicy) {
        if (prev.luggage_type.includes('carry-on') && baggagePolicy.carryOn?.weight) {
          carryOnWeight = extractWeight(baggagePolicy.carryOn.weight);
        }
        if (prev.luggage_type.includes('checked') && baggagePolicy.checked?.weight) {
          checkedWeight = extractWeight(baggagePolicy.checked.weight);
        }
      }
      
      return {
        ...prev,
        airline,
        airline_baggage_policy: baggagePolicy,
        carry_on_weight: carryOnWeight,
        checked_weight: checkedWeight
      };
    });
    setAirlineInput(airline);
    setShowAirlineSuggestions(false);
  }, [getBaggagePolicy, extractWeight]);

  const handleAirlineChange = useCallback((value: string) => {
    setAirlineInput(value);
    
    if (value.trim().length > 0) {
      // Check if exact match exists in database airline names
      const airlineNames = getAirlineNames();
      const exactMatch = airlineNames.find(name => name.toLowerCase() === value.toLowerCase());
      if (exactMatch) {
        const baggagePolicy = getBaggagePolicy(exactMatch, formData.travel_class || 'economy');
        
        let carryOnWeight = formData.carry_on_weight;
        let checkedWeight = formData.checked_weight;

        // Auto-fill weight limits if baggage policy exists
        if (baggagePolicy) {
          if (formData.luggage_type.includes('carry-on') && baggagePolicy.carryOn?.weight) {
            carryOnWeight = extractWeight(baggagePolicy.carryOn.weight);
          }
          if (formData.luggage_type.includes('checked') && baggagePolicy.checked?.weight) {
            checkedWeight = extractWeight(baggagePolicy.checked.weight);
          }
        }
        
        setFormData(prev => ({
          ...prev,
          airline: value,
          airline_baggage_policy: baggagePolicy,
          carry_on_weight: carryOnWeight,
          checked_weight: checkedWeight
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        airline: '',
        airline_baggage_policy: undefined
      }));
    }
    
    setShowAirlineSuggestions(value.trim().length > 0);
  }, [getBaggagePolicy, getAirlineNames, formData.travel_class, formData.luggage_type, formData.carry_on_weight, formData.checked_weight, extractWeight]);

  // Handle travel class change and update baggage policy
  const handleTravelClassChange = useCallback((travelClass: string) => {
    setFormData(prev => {
      const baggagePolicy = prev.airline ? getBaggagePolicy(prev.airline, travelClass) : undefined;
      
      let carryOnWeight = prev.carry_on_weight;
      let checkedWeight = prev.checked_weight;

      // Auto-fill weight limits if baggage policy exists and fields are empty or user wants to update
      if (baggagePolicy) {
        if (prev.luggage_type.includes('carry-on') && baggagePolicy.carryOn?.weight) {
          const newCarryOnWeight = extractWeight(baggagePolicy.carryOn.weight);
          if (newCarryOnWeight && (!prev.carry_on_weight || prev.carry_on_weight === '0')) {
            carryOnWeight = newCarryOnWeight;
          }
        }
        if (prev.luggage_type.includes('checked') && baggagePolicy.checked?.weight) {
          const newCheckedWeight = extractWeight(baggagePolicy.checked.weight);
          if (newCheckedWeight && (!prev.checked_weight || prev.checked_weight === '0')) {
            checkedWeight = newCheckedWeight;
          }
        }
      }
      
      return {
        ...prev,
        travel_class: travelClass,
        airline_baggage_policy: baggagePolicy,
        carry_on_weight: carryOnWeight,
        checked_weight: checkedWeight
      };
    });
  }, [getBaggagePolicy, extractWeight]);

  // Memoize luggage type string to prevent infinite loops
  const luggageTypeKey = useMemo(() => formData.luggage_type.sort().join(','), [formData.luggage_type]);

  // Keep baggage policy live with DB updates
  useEffect(() => {
    if (!formData.airline || !formData.travel_class) return;
    const policy = getBaggagePolicy(formData.airline, formData.travel_class);
    setFormData(prev => {
      // Check if policy actually changed to prevent unnecessary updates
      const existingPolicy = prev.airline_baggage_policy;
      const policyUnchanged = existingPolicy && policy && 
        existingPolicy.carryOn?.weight === policy.carryOn?.weight &&
        existingPolicy.checked?.weight === policy.checked?.weight;
      
      if (!policy) {
        if (!existingPolicy) return prev; // No change needed
        return { ...prev, airline_baggage_policy: undefined };
      }
      
      if (policyUnchanged) return prev; // No change needed
      
      let carryOnWeight = prev.carry_on_weight;
      let checkedWeight = prev.checked_weight;
      if (prev.luggage_type.includes('carry-on') && policy.carryOn?.weight && (!carryOnWeight || carryOnWeight === '0')) {
        const w = extractWeight(policy.carryOn.weight);
        if (w) carryOnWeight = w;
      }
      if (prev.luggage_type.includes('checked') && policy.checked?.weight && (!checkedWeight || checkedWeight === '0')) {
        const w = extractWeight(policy.checked.weight);
        if (w) checkedWeight = w;
      }
      return {
        ...prev,
        airline_baggage_policy: policy,
        carry_on_weight: carryOnWeight,
        checked_weight: checkedWeight
      };
    });
  }, [airlines, formData.airline, formData.travel_class, luggageTypeKey, getBaggagePolicy, extractWeight]);

  // Enhanced weather data using live API and seasonal data
  const fetchWeatherData = async () => {
    if (!formData.destination || !formData.start_date || !formData.end_date) return null;
    
    try {
      const { fetchLiveWeather } = await import('../../utils/weatherService');
      return await fetchLiveWeather(formData.destination, formData.start_date, formData.end_date);
    } catch (error) {
      console.error('Failed to fetch weather data:', error);
      // Fallback to basic seasonal data with isoDate for matching
      const startDate = formData.start_date!;
      const dailyData = Array.from({ length: 5 }, (_, i) => {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        const isoDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return { 
          date: format(d, 'EEE, MMM d'), 
          isoDate,
          high: `${24 - i}°C`, 
          low: `${16 - i}°C`, 
          condition: ['Pleasant', 'Partly cloudy', 'Sunny', 'Cloudy', 'Clear'][i], 
          precipitation: `${25 + i * 5}%`, 
          icon: ['⛅', '⛅', '☀️', '☁️', '☀️'][i] 
        };
      });
      return {
        current: { temp: '22°C', condition: 'Pleasant', precipitation: '30%', icon: '⛅' },
        alerts: [],
        daily: dailyData,
        isLiveWeather: false,
        forecastNote: `Seasonal forecast for ${formData.destination}`
      };
    }
  };

  // Fetch user preferences including temperature unit
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setUserPreferences(data);
      }
    };

    fetchPreferences();
  }, [user]);

  // Convert weather data to user's preferred temperature unit
  useEffect(() => {
    if (!weatherData) {
      setDisplayWeatherData(null);
      return;
    }

    const tempUnit: TemperatureUnit = userPreferences?.temperature_unit || 'celsius';
    const converted = convertWeatherData(weatherData, tempUnit);
    setDisplayWeatherData(converted);
  }, [weatherData, userPreferences?.temperature_unit]);

  useEffect(() => {
    if (formData.destination && formData.start_date && formData.end_date) {
      console.log('Weather effect triggered for:', formData.destination);
      fetchWeatherData().then((data) => {
        console.log('Setting weather data:', data);
        setWeatherData(data);
      });
    } else {
      setWeatherData(null);
    }
  }, [formData.destination, formData.start_date, formData.end_date]);

  const getDailyForecastForTrip = () => {
    if (!displayWeatherData || !formData.start_date || !formData.end_date) return [];
    
    const expectedDays = getTripLength();
    if (expectedDays <= 0) return [];

    const forecasts = displayWeatherData.daily || [];
    
    // If no forecasts available, return empty
    if (forecasts.length === 0) return [];

    // Build exact date keys (YYYY-MM-DD) for the trip range in UTC to avoid drift
    const keys: string[] = [];
    const d = new Date(Date.UTC(formData.start_date.getFullYear(), formData.start_date.getMonth(), formData.start_date.getDate()));
    for (let i = 0; i < expectedDays; i++) {
      const k = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      keys.push(k);
      d.setUTCDate(d.getUTCDate() + 1);
    }

    // Map each required date to a forecast
    const result: any[] = [];
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      // Try exact isoDate match first
      let match = forecasts.find((f: any) => f.isoDate === k);
      
      // If no exact match, use the forecast at the same index (for seasonal data)
      // or the last available forecast
      if (!match) {
        match = forecasts[i] || forecasts[forecasts.length - 1];
      }
      
      if (match) {
        // Update the date display to match the actual trip date
        const tripDate = new Date(formData.start_date!);
        tripDate.setDate(tripDate.getDate() + i);
        result.push({
          ...match,
          isoDate: k,
          date: format(tripDate, 'EEE, MMM d')
        });
      }
    }

    return result.slice(0, expectedDays);
  };

  const handleLuggageTypeChange = (type: string, checked: boolean) => {
    setFormData(prev => {
      const nextLuggage = checked
        ? [...prev.luggage_type, type]
        : prev.luggage_type.filter(t => t !== type);

      // If airline selected, try to auto-fill weights for newly added luggage types
      let carryOnWeight = prev.carry_on_weight;
      let checkedWeight = prev.checked_weight;
      const baggagePolicy = prev.airline ? getBaggagePolicy(prev.airline, prev.travel_class || 'economy') : prev.airline_baggage_policy;

      if (checked && baggagePolicy) {
        if (type === 'carry-on' && baggagePolicy.carryOn?.weight) {
          const w = extractWeight(baggagePolicy.carryOn.weight);
          if (w) carryOnWeight = w;
        }
        if (type === 'checked' && baggagePolicy.checked?.weight) {
          const w = extractWeight(baggagePolicy.checked.weight);
          if (w) checkedWeight = w;
        }
      }

      return {
        ...prev,
        luggage_type: nextLuggage,
        airline_baggage_policy: baggagePolicy || prev.airline_baggage_policy,
        carry_on_weight: carryOnWeight,
        checked_weight: checkedWeight,
      };
    });
  };

  const getTripLength = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    return differenceInDays(formData.end_date, formData.start_date) + 1;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.destination || !formData.start_date || !formData.end_date || !formData.trip_type) {
      return;
    }
    
    // Check and record usage for free tier users
    if (subscription.subscription_tier === 'economy') {
      const canUse = await recordUsage('packing_list');
      if (!canUse) {
        return; // Usage limit reached, toast already shown
      }
    }
    
    setCurrentStep('loading');
  };

  const handleLoadingComplete = () => {
    setCurrentStep('packing');
  };

  const fetchUserPreferences = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user preferences:', error);
        return;
      }
      
      setUserPreferences(data);
    } catch (err) {
      console.error('Error fetching user preferences:', err);
    }
  };

  useEffect(() => {
    fetchUserPreferences();
  }, [user]);

  const handlePackingItemsChange = (items: any[]) => {
    setPackingItems(items);
  };

  const handlePackingComplete = async () => {
    // Save packing list to trip if one is selected or create new trip
    try {
      if (!user) {
        setCurrentStep('luggage-animation');
        return;
      }

      const packingListData = {
        destination: formData.destination,
        destination_country: formData.destination_country,
        tripType: formData.trip_type,
        luggageTypes: formData.luggage_type,
        airline: formData.airline,
        travelClass: formData.travel_class,
        items: packingItems,
        savedAt: new Date().toISOString()
      };

      if (selectedTripId === 'new') {
        // Create a new trip with the packing list
        const { error } = await supabase
          .from('trips')
          .insert([{
            user_id: user.id,
            title: `Trip to ${formData.destination}`,
            destination: formData.destination,
            destination_country: formData.destination_country,
            start_date: formData.start_date?.toISOString().split('T')[0],
            end_date: formData.end_date?.toISOString().split('T')[0],
            trip_type: formData.trip_type as any,
            packing_list: packingListData as any,
            currency: 'USD',
            is_active: true
          }]);

        if (error) throw error;
        toast({ title: 'Success', description: 'Packing list saved to new trip!' });
      } else {
        // Update existing trip with packing list
        const { error } = await supabase
          .from('trips')
          .update({ packing_list: packingListData as any })
          .eq('id', selectedTripId);

        if (error) throw error;
        toast({ title: 'Success', description: 'Packing list saved to trip!' });
      }
    } catch (error) {
      console.error('Error saving packing list:', error);
      toast({ title: 'Error', description: 'Failed to save packing list', variant: 'destructive' });
    }

    setCurrentStep('luggage-animation');
  };

  const handleLoadPreviousList = (list: any) => {
    const packingData = list.packingList;
    
    // Pre-fill form with data from previous trip
    setFormData(prev => ({
      ...prev,
      destination: list.destination,
      trip_type: packingData.tripType || '',
      luggage_type: packingData.luggageTypes || [],
      airline: packingData.airline || '',
      travel_class: packingData.travelClass || 'economy'
    }));
    
    setDestinationInput(list.destination);
    if (packingData.airline) {
      setAirlineInput(packingData.airline);
    }
    
    setShowPreviousListsModal(false);
    toast({ title: 'Success', description: 'Previous packing list loaded!' });
  };

  const handleLuggageAnimationComplete = () => {
    setCurrentStep('complete');
  };

  const handleStartOver = () => {
    setCurrentStep('form');
    setFormData({
      destination: '',
      destination_country: '',
      start_date: undefined,
      end_date: undefined,
      trip_type: '',
      travel_class: '',
      luggage_type: [],
      weight_unit: 'kg',
      carry_on_weight: '',
      checked_weight: '',
      airline: ''
    });
    setDestinationInput('');
    setAirlineInput('');
    setWeatherData(null);
  };

  // If multi-city mode is active and user has access, show the multi-city workflow
  if (showMultiCityMode && !isMultiCityLocked) {
    return <MultiCityPackingWorkflow onComplete={() => setShowMultiCityMode(false)} />;
  }

  if (currentStep === 'luggage-animation') {
    return <LuggageCompletionAnimation onComplete={handleLuggageAnimationComplete} />;
  }

  if (currentStep === 'complete') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="p-8">
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-r from-primary to-primary/70 rounded-full flex items-center justify-center">
                  <PartyPopper className="w-10 h-10 text-primary-foreground" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent rounded-full animate-ping"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-foreground">Packing Complete! 🎉</h2>
              <p className="text-lg text-muted-foreground">
                Your bag is ready for {formData.destination}!
              </p>
            </div>
            
            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-foreground">Trip Summary</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p><span className="font-medium">Destination:</span> {formData.destination}, {formData.destination_country}</p>
                <p><span className="font-medium">Duration:</span> {getTripLength()} days</p>
                <p><span className="font-medium">Trip Type:</span> {formData.trip_type.charAt(0).toUpperCase() + formData.trip_type.slice(1)}</p>
                {formData.luggage_type.length > 0 && (
                  <p><span className="font-medium">Luggage:</span> {formData.luggage_type.map(type => type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-')).join(', ')}</p>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <Button onClick={handleStartOver} className="w-full">
                Pack Another Bag
              </Button>
              <Button variant="outline" onClick={onComplete} className="w-full">
                Return to Dashboard
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (currentStep === 'loading') {
    return <PackingLoadingAnimation onComplete={handleLoadingComplete} />;
  }

  if (currentStep === 'packing') {
    const dailyForecasts = getDailyForecastForTrip();
    
    return (
      <div className="space-y-6">
        <Card className="border-cyan-500/20 shadow-xl shadow-cyan-500/5 overflow-hidden">
          <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent px-4 sm:px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/30">
                <Luggage className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold leading-tight truncate">
                  Packing for {formData.destination}
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  {getTripLength()} day {formData.trip_type} trip
                  {formData.start_date && formData.end_date && (
                    <> · {format(formData.start_date, 'MMM d')} – {format(formData.end_date, 'MMM d')}</>
                  )}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Daily Weather Forecast Section - Always show when dates are selected */}
        {formData.destination && formData.start_date && formData.end_date && (
          <Card className="border-cyan-500/20 shadow-xl shadow-cyan-500/5 overflow-hidden">
            <div className="bg-gradient-to-br from-cyan-50/70 via-teal-50/30 to-transparent dark:from-cyan-950/20 dark:via-teal-950/10 dark:to-transparent px-4 sm:px-6 pt-5 pb-4 border-b border-border/40">
              <div className="flex items-center gap-3 mb-1">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/20 text-base">
                  🌤️
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base sm:text-lg font-bold">Daily Weather Forecast</h3>
                  {weatherData?.isLiveWeather ? (
                    <Badge className="text-[10px] bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/15">Live Data</Badge>
                  ) : weatherData && (
                    <Badge variant="outline" className="text-[10px] border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                      Seasonal Forecast
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground pl-12">
                {displayWeatherData?.isLiveWeather
                  ? `Live forecast for ${format(formData.start_date!, 'MMM d')} – ${format(formData.end_date!, 'MMM d')} · updates every 4h`
                  : displayWeatherData?.forecastNote || `Weather forecast for ${formData.destination} during your trip`
                }
                {displayWeatherData?.isLiveWeather && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setWeatherData(null);
                      fetchWeatherData().then(setWeatherData);
                    }}
                    className="ml-2 text-xs h-6 hover:text-cyan-600 hover:bg-cyan-500/10"
                  >
                    🔄 Refresh
                  </Button>
                )}
              </p>
            </div>
            <CardContent className="space-y-4 sm:space-y-6 pt-5">

              {/* Loading state */}
              {!displayWeatherData ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-pulse text-2xl">🌤️</div>
                    <p className="text-sm text-muted-foreground mt-2">Loading weather forecast...</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Weather Alerts */}
                  {displayWeatherData.alerts && displayWeatherData.alerts.length > 0 && (
                    <div className="p-3 sm:p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-amber-600 dark:text-amber-400 flex-shrink-0">⚠️</span>
                        <div className="min-w-0">
                          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-1 text-sm sm:text-base">Weather Alerts</h4>
                          <ul className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 space-y-1">
                            {displayWeatherData.alerts.map((alert: string, index: number) => (
                              <li key={index} className="break-words">• {alert}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Daily Forecast Grid */}
                  <TooltipProvider delayDuration={200}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
                      {dailyForecasts.map((day, index) => (
                        <div 
                          key={index} 
                          className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-cyan-50/60 to-teal-50/30 dark:from-cyan-950/15 dark:to-teal-950/10 border border-cyan-500/15 hover:border-cyan-500/30 hover:shadow-md hover:shadow-cyan-500/10 transition-all duration-200"
                        >
                          <div className="text-center space-y-1 sm:space-y-1.5">
                            {/* Date with clear day and date format */}
                            <div className="text-xs sm:text-sm font-medium text-foreground">
                              <div className="font-semibold">{day.date.split(',')[0]}</div>
                              <div className="text-muted-foreground">{day.date.split(',')[1]?.trim()}</div>
                            </div>
                            
                            {/* Weather Icon */}
                            <div className="text-xl sm:text-2xl">
                              {day.icon}
                            </div>
                            
                            {/* Temperature */}
                            <div className="space-y-0.5">
                              <div className="flex justify-center items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                                <span className="font-semibold text-foreground">{day.high}</span>
                                <span className="text-muted-foreground">{day.low}</span>
                              </div>
                              <div className="text-xs text-muted-foreground hidden sm:block">High / Low</div>
                            </div>
                            
                            {/* Condition */}
                            <div className="text-xs font-medium text-foreground leading-tight">
                              {day.condition}
                            </div>
                            
                            {/* Precipitation with Tooltip */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground cursor-help transition-colors hover:text-foreground">
                                  <span>💧</span>
                                  <span>{day.precipitation}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                <p>Chance of Rain</p>
                              </TooltipContent>
                            </Tooltip>
                            
                            {/* Humidity with Tooltip */}
                            {day.humidity && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground cursor-help transition-colors hover:text-foreground">
                                    <span>💦</span>
                                    <span>{day.humidity}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  <p>Humidity Level</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            {/* Wind Speed with Tooltip */}
                            {day.windSpeed && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground cursor-help transition-colors hover:text-foreground">
                                    <span>💨</span>
                                    <span>{day.windSpeed}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  <p>Wind Speed</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            {/* Feels Like Temperatures */}
                            {(day.feelsLikeHigh || day.feelsLikeLow) && (
                              <div className="text-[10px] sm:text-xs text-muted-foreground space-y-0.5">
                                {day.feelsLikeHigh && (
                                  <div className="flex justify-between">
                                    <span>Feels like:</span>
                                    <span>{day.feelsLikeHigh}</span>
                                  </div>
                                )}
                                {day.feelsLikeLow && (
                                  <div className="flex justify-between">
                                    <span>Feels like:</span>
                                    <span>{day.feelsLikeLow}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TooltipProvider>

                  {/* Packing Tips Based on Weather */}
                  <div className="p-3 sm:p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-green-600 dark:text-green-400 flex-shrink-0">💡</span>
                      <div className="min-w-0">
                        <h4 className="font-medium text-green-800 dark:text-green-200 mb-2 text-sm sm:text-base">Weather-Based Packing Tips</h4>
                        <div className="text-xs sm:text-sm text-green-700 dark:text-green-300 space-y-1">
                          {dailyForecasts.some(day => parseInt(day.precipitation) > 50) && (
                            <p className="break-words">• Pack a waterproof jacket or umbrella - rain expected during your trip</p>
                          )}
                          {dailyForecasts.some(day => day.condition.toLowerCase().includes('hot') || parseInt(day.high) > 30) && (
                            <p className="break-words">• Bring lightweight, breathable clothing and sun protection</p>
                          )}
                          {dailyForecasts.some(day => parseInt(day.low) < 10) && (
                            <p className="break-words">• Pack warm layers - temperatures will drop in the evenings</p>
                          )}
                          {dailyForecasts.some(day => day.condition.toLowerCase().includes('storm')) && (
                            <p className="break-words">• Consider indoor activities and pack entertainment for stormy weather</p>
                          )}
                          <p className="break-words">• Check weather conditions daily and adjust your outfit plans accordingly</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <PackingList 
              destination={formData.destination}
              country={formData.destination_country}
              tripType={formData.trip_type}
              tripLength={getTripLength()}
              startDate={formData.start_date}
              endDate={formData.end_date}
              luggageTypes={formData.luggage_type}
              weatherData={weatherData}
              weightUnit={formData.weight_unit}
              carryOnLimit={parseFloat(formData.carry_on_weight) || 0}
              checkedLimit={parseFloat(formData.checked_weight) || 0}
              airlineBaggagePolicy={formData.airline_baggage_policy}
              onComplete={handlePackingComplete}
              onItemsChange={handlePackingItemsChange}
            />
          </div>
          
          <div className="space-y-6">
            <ForgottenItemsReminder
              currentDestination={formData.destination_country}
              currentTripType={formData.trip_type}
              onAddItem={(itemName) => {
                // Add item to packing list
                setPackingItems(prev => [...prev, { name: itemName, packed: false, category: 'Essentials' }]);
              }}
            />
            
            <WeightEstimator 
              luggageTypes={formData.luggage_type}
              carryOnLimit={parseFloat(formData.carry_on_weight) || 0}
              checkedLimit={parseFloat(formData.checked_weight) || 0}
              weightUnit={formData.weight_unit}
              airline={formData.airline}
            />
            
            <PackingTimeEstimator
              tripLength={getTripLength()}
              destination={formData.destination}
              tripType={formData.trip_type}
              packingItems={packingItems}
              startDate={formData.start_date || new Date()}
              endDate={formData.end_date || new Date()}
              packingStyle={userPreferences?.packing_style || "balanced"}
              travelingWith="solo"
            />
            
            <PackingTips 
              tripType={formData.trip_type}
              destination={formData.destination}
              tripLength={getTripLength()}
              luggageConfig={formData.luggage_type}
            />
            
            <PreTripLaundry />
            
            <TemplateManager />
            
            <OfflinePackingManager />
            
            <GroupPackingSharing />
            
            <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
              <CardHeader className="pb-3 bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                    <MapPin className="h-4 w-4" />
                  </span>
                  Visa Requirements
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Check if you need a visa for {formData.destination}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-3">
                <Button 
                  onClick={() => setShowVisaModal(true)}
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0 shadow-md shadow-cyan-500/20"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Check Visa Requirements
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <VisaCheckModal
          isOpen={showVisaModal}
          onClose={() => setShowVisaModal(false)}
          destination={formData.destination}
          destinationCountry={formData.destination_country}
        />
      </div>
    );
  }

  return (
    <>
      <Card className="w-full max-w-2xl mx-auto border-cyan-500/20 shadow-xl shadow-cyan-500/5 overflow-hidden">
        {/* Hero header with gradient */}
        <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent px-4 sm:px-6 pt-5 pb-5 border-b border-border/50">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/30">
                <Luggage className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold leading-tight">Pack My Bag</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {stepDescriptions[formStep - 1]}
                </p>
              </div>
            </div>
            {previousLists.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreviousListsModal(true)}
                className="gap-1.5 shrink-0 text-muted-foreground hover:text-cyan-600 hover:bg-cyan-500/10"
              >
                <History className="h-4 w-4" />
                <span className="hidden sm:inline text-xs">Previous</span>
              </Button>
            )}
          </div>

          {/* Numbered step circles with connecting line */}
          <div className="relative">
            <div className="flex items-center justify-between relative">
              {/* Background line */}
              <div className="absolute left-0 right-0 top-4 sm:top-5 h-0.5 bg-muted -z-0" />
              {/* Progress line */}
              <div
                className="absolute left-0 top-4 sm:top-5 h-0.5 bg-gradient-to-r from-cyan-400 to-teal-500 -z-0 transition-all duration-500"
                style={{ width: `${((formStep - 1) / (totalSteps - 1)) * 100}%` }}
              />
              {stepLabels.map((label, idx) => {
                const stepNum = (idx + 1) as 1 | 2 | 3 | 4;
                const isActive = formStep === stepNum;
                const isComplete = formStep > stepNum;
                const isClickable = isComplete;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => isClickable && setFormStep(stepNum)}
                    disabled={!isClickable}
                    className={cn(
                      "relative z-10 flex flex-col items-center gap-1.5 group",
                      isClickable && "cursor-pointer"
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full text-sm font-semibold transition-all duration-300 border-2",
                        isActive &&
                          "bg-gradient-to-br from-cyan-400 to-teal-500 text-white border-transparent shadow-lg shadow-cyan-500/30 scale-110",
                        isComplete &&
                          "bg-gradient-to-br from-cyan-400 to-teal-500 text-white border-transparent group-hover:scale-105",
                        !isActive && !isComplete &&
                          "bg-background text-muted-foreground border-muted"
                      )}
                    >
                      {isComplete ? <Check className="h-4 w-4" /> : stepNum}
                    </span>
                    <span
                      className={cn(
                        "text-[10px] sm:text-xs font-medium transition-colors text-center max-w-[60px] sm:max-w-none leading-tight",
                        isActive
                          ? "text-cyan-600 dark:text-cyan-400 font-semibold"
                          : isComplete
                          ? "text-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      <CardContent className="px-4 sm:px-6">
        <form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
          {/* Link to Trip - Collapsible (always visible) */}
          <Collapsible open={isTripSectionOpen} onOpenChange={setIsTripSectionOpen}>
            <div className="rounded-md border border-dashed border-border/60 bg-transparent overflow-hidden">
              <CollapsibleTrigger className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/40 transition-colors">
                <div className="flex items-center gap-2 text-left min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">
                    <span className="text-foreground/80">
                      {selectedTripId === 'new'
                        ? 'New trip'
                        : userTrips.find(t => t.id === selectedTripId)?.title || 'Save to a trip'}
                    </span>
                    <span className="hidden sm:inline"> · {selectedTripId === 'new' ? 'a trip will be created' : selectedTripId ? 'linked' : 'optional'}</span>
                  </p>
                </div>
                <ChevronDown className={cn(
                  "h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0",
                  isTripSectionOpen && "transform rotate-180"
                )} />
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-3">
                  {/* Create New Trip Option */}
                  <div 
                    onClick={() => setSelectedTripId('new')}
                    className={cn(
                      "relative flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                      selectedTripId === 'new' 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className="flex items-center h-5">
                      <div className={cn(
                        "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                        selectedTripId === 'new' ? "border-primary" : "border-muted-foreground"
                      )}>
                        {selectedTripId === 'new' && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4 text-primary" />
                        <span className="font-medium text-sm">Create New Trip</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        This will create a new trip with your packing list
                      </p>
                    </div>
                  </div>

                  {/* Existing Trips */}
                  {userTrips.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground px-1">
                        Or link to existing trip:
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userTrips.map(trip => (
                          <div
                            key={trip.id}
                            onClick={() => setSelectedTripId(trip.id)}
                            className={cn(
                              "relative flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                              selectedTripId === trip.id 
                                ? "border-primary bg-primary/5" 
                                : "border-border hover:border-primary/50"
                            )}
                          >
                            <div className="flex items-center h-5">
                              <div className={cn(
                                "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                selectedTripId === trip.id ? "border-primary" : "border-muted-foreground"
                              )}>
                                {selectedTripId === trip.id && (
                                  <div className="w-2 h-2 rounded-full bg-primary" />
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm truncate">{trip.title}</p>
                                  <p className="text-xs text-muted-foreground truncate">{trip.destination}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {format(new Date(trip.start_date), 'MMM d')} - {format(new Date(trip.end_date), 'MMM d, yyyy')}
                                  </p>
                                </div>
                                {trip.packing_list && (
                                  <Badge variant="secondary" className="text-xs shrink-0">
                                    Has list
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* STEP 1: Destination */}
          {formStep === 1 && (
          <div className="space-y-4 animate-fade-in">
          {/* Destination */}
          <div className="space-y-2">
            <Label htmlFor="destination" className="flex items-center gap-1 text-sm sm:text-base">
              Where are you going? 
              <span className="text-destructive">*</span>
            </Label>
            <Popover open={showCitySuggestions && filteredDestinations.length > 0} onOpenChange={() => {}}>
              <PopoverTrigger asChild>
                <div className="relative">
                  <Input
                    id="destination"
                    value={destinationInput}
                    onChange={(e) => handleDestinationChange(e.target.value)}
                    onFocus={handleDestinationFocus}
                    onBlur={(e) => {
                      // Delay hiding suggestions to allow selection
                      setTimeout(() => {
                        const activeElement = document.activeElement;
                        const currentTarget = e.currentTarget;
                        if (!activeElement || !currentTarget || !currentTarget.contains(activeElement)) {
                          setShowCitySuggestions(false);
                        }
                      }, 150);
                    }}
                    placeholder="Search for cities, islands, or countries..."
                    required
                    className="pr-10 text-sm font-normal h-9"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="text"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 pointer-events-none" />
                </div>
              </PopoverTrigger>
              <PopoverContent 
                className="w-full p-0 z-50 bg-popover border shadow-md" 
                align="start"
                style={{ width: 'var(--radix-popover-trigger-width)' }}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <Command className="w-full">
                  <CommandList>
                    {filteredDestinations.length > 0 ? (
                      <CommandGroup>
                        {filteredDestinations.map((destination, index) => (
                          <CommandItem
                            key={`${destination.name}-${destination.country}-${index}`}
                            value={`${destination.name}, ${destination.country}`}
                            onSelect={() => selectCity(destination)}
                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <MapPin className="h-4 w-4 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {destination.name}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {destination.country}
                                </div>
                              </div>
                            </div>
                            {formData.destination === destination.name && formData.destination_country === destination.country && (
                              <Check className="h-3 w-3 text-primary" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ) : destinationInput.trim().length > 0 ? (
                      <CommandEmpty className="p-4 text-center text-sm text-muted-foreground">
                        No destinations found. Try a different search term.
                      </CommandEmpty>
                    ) : null}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Multi-City Option */}
            <div 
              onClick={() => {
                if (isMultiCityLocked) {
                  // Show upgrade prompt in a toast or modal
                  toast({
                    title: 'Premium Feature',
                    description: 'Multi-city packing is available with First Class Pack. Upgrade to unlock!',
                  });
                } else {
                  setShowMultiCityMode(true);
                }
              }}
              className={cn(
                "mt-2 p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-3",
                isMultiCityLocked 
                  ? "border-border bg-muted/30 hover:bg-muted/50" 
                  : "border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50"
              )}
            >
              <div className={cn(
                "p-2 rounded-lg",
                isMultiCityLocked ? "bg-muted" : "bg-gradient-to-br from-yellow-500 to-teal-500"
              )}>
                <Plane className={cn(
                  "h-4 w-4",
                  isMultiCityLocked ? "text-muted-foreground" : "text-white"
                )} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Planning a multi-city trip?</span>
                  {isMultiCityLocked && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
                <p className="text-xs text-muted-foreground">
                  {isMultiCityLocked 
                    ? "Upgrade to First Class to pack for multiple destinations"
                    : "Create packing lists for multiple destinations at once"
                  }
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          </div>
          </div>
          )}

          {/* STEP 2: Dates */}
          {formStep === 2 && (
          <div className="space-y-4 animate-fade-in">
          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm sm:text-base">
                Travel Dates
                <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.start_date && formData.end_date 
                      ? `${format(formData.start_date, "MMM d")} - ${format(formData.end_date, "MMM d, yyyy")}`
                      : formData.start_date 
                        ? `${format(formData.start_date, "PPP")} - Select end date`
                        : "Select your travel dates"
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="range"
                    selected={{
                      from: formData.start_date,
                      to: formData.end_date
                    }}
                    onSelect={(range) => {
                      setFormData(prev => ({
                        ...prev,
                        start_date: range?.from,
                        end_date: range?.to
                      }));
                    }}
                    initialFocus
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    className={cn("p-3 pointer-events-auto")}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          </div>
          )}

          {/* STEP 3: Trip Type */}
          {formStep === 3 && (
          <div className="space-y-4 animate-fade-in">
          {/* Trip Type */}
          <div className="space-y-2">
            <Label htmlFor="trip_type" className="flex items-center gap-1 text-sm sm:text-base">
              What type of trip is this?
              <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.trip_type} onValueChange={(value) => setFormData(prev => ({ ...prev, trip_type: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select trip type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leisure">Leisure</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="adventure">Adventure</SelectItem>
                <SelectItem value="family">Family</SelectItem>
                <SelectItem value="romantic">Romantic</SelectItem>
                <SelectItem value="solo">Solo Travel</SelectItem>
                <SelectItem value="beach">Beach Vacation</SelectItem>
                <SelectItem value="city">City Break</SelectItem>
                <SelectItem value="backpacking">Backpacking</SelectItem>
                <SelectItem value="luxury">Luxury Travel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          </div>
          )}

          {/* STEP 4: Luggage + Summary + Submit */}
          {formStep === 4 && (
          <div className="space-y-4 animate-fade-in">
          {/* Luggage Configuration */}
          <div className="space-y-4">
            <Label className="text-sm sm:text-base">Luggage Configuration</Label>
            
            <div className="space-y-3">
              <Label className="text-xs sm:text-sm font-medium">What luggage will you use?</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="carry-on"
                    checked={formData.luggage_type.includes('carry-on')}
                    onCheckedChange={(checked) => handleLuggageTypeChange('carry-on', checked as boolean)}
                  />
                  <Label htmlFor="carry-on" className="text-sm font-normal">Carry-on bag</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="checked"
                    checked={formData.luggage_type.includes('checked')}
                    onCheckedChange={(checked) => handleLuggageTypeChange('checked', checked as boolean)}
                  />
                  <Label htmlFor="checked" className="text-sm font-normal">Checked bag</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="personal"
                    checked={formData.luggage_type.includes('personal')}
                    onCheckedChange={(checked) => handleLuggageTypeChange('personal', checked as boolean)}
                  />
                  <Label htmlFor="personal" className="text-sm font-normal">Personal item (backpack, purse)</Label>
                </div>
              </div>
            </div>

            {formData.luggage_type.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2 relative">
                  <Label className="text-sm">Airline (optional)</Label>
                  <div className="relative">
                    <Input
                      value={airlineInput}
                      onChange={(e) => handleAirlineChange(e.target.value)}
                      onFocus={() => {
                        if (airlineInput.trim().length > 0) {
                          setShowAirlineSuggestions(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowAirlineSuggestions(false), 200)}
                      placeholder="Search airlines..."
                      className="h-8 text-sm"
                      autoComplete="off"
                    />
                    {showAirlineSuggestions && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                        {airlinesLoading && (
                          <div className="px-3 py-2 text-xs text-muted-foreground">Loading airlines…</div>
                        )}
                        {!airlinesLoading && filteredAirlines.length > 0 && (
                          <>
                            {filteredAirlines.map((airline) => (
                              <button
                                key={airline}
                                type="button"
                                className="w-full px-3 py-1.5 text-left hover:bg-muted transition-colors text-xs border-b border-border/30 last:border-b-0"
                                onClick={() => selectAirline(airline)}
                              >
                                <div className="font-medium">{airline}</div>
                              </button>
                            ))}
                          </>
                        )}
                        {!airlinesLoading && filteredAirlines.length === 0 && airlineInput.trim().length > 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground">No airlines found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-1">
                    Travel Class
                  </Label>
                  <Select value={formData.travel_class} onValueChange={handleTravelClassChange}>
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select travel class" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border rounded-md shadow-lg z-50">
                      <SelectItem value="economy">Economy</SelectItem>
                      <SelectItem value="premium-economy">Premium Economy</SelectItem>
                      <SelectItem value="business">Business Class</SelectItem>
                      <SelectItem value="first">First Class</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Weight Unit</Label>
                  <Select value={formData.weight_unit} onValueChange={(value: 'kg' | 'lbs') => setFormData(prev => ({ ...prev, weight_unit: value }))}>
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">Kilograms (kg)</SelectItem>
                      <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Airline Baggage Policy Display */}
                {formData.airline_baggage_policy && (formData.luggage_type.includes('carry-on') || formData.luggage_type.includes('checked')) && (
                  <div className="col-span-full bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Weight className="h-4 w-4 text-primary" />
                        <h4 className="font-medium text-sm text-primary">{formData.airline} Baggage Allowance</h4>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {formData.travel_class?.charAt(0).toUpperCase() + formData.travel_class?.slice(1).replace('-', ' ')}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {formData.luggage_type.includes('carry-on') && formData.airline_baggage_policy.carryOn && (
                        <div className="bg-background/60 rounded-md p-3 space-y-2 border border-green-500/20">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="font-semibold text-foreground">Carry-On Bag</div>
                          </div>
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Max Weight:</span>
                              <span className="font-semibold text-foreground">{formData.airline_baggage_policy.carryOn.weight}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Max Size:</span>
                              <span className="font-semibold text-foreground text-right">{formData.airline_baggage_policy.carryOn.dimensions}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {formData.luggage_type.includes('checked') && formData.airline_baggage_policy.checked && (
                        <div className="bg-background/60 rounded-md p-3 space-y-2 border border-cyan-500/20">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                            <div className="font-semibold text-foreground">Checked Bag</div>
                          </div>
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Max Weight:</span>
                              <span className="font-semibold text-foreground">{formData.airline_baggage_policy.checked.weight}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Max Size:</span>
                              <span className="font-semibold text-foreground text-right">{formData.airline_baggage_policy.checked.dimensions}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {formData.luggage_type.includes('personal') && formData.airline_baggage_policy.personal && (
                        <div className="bg-background/60 rounded-md p-3 space-y-2 border border-cyan-500/20">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                            <div className="font-semibold text-foreground">Personal Item</div>
                          </div>
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Max Weight:</span>
                              <span className="font-semibold text-foreground">{formData.airline_baggage_policy.personal.weight}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Max Size:</span>
                              <span className="font-semibold text-foreground text-right">{formData.airline_baggage_policy.personal.dimensions}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground pt-2 border-t border-border/50 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <Info className="h-3 w-3 flex-shrink-0" />
                        <span>Policies may vary by route and fare type. Always verify before travel.</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm">Weight Limits</Label>
                  <div className="space-y-1">
                    {formData.luggage_type.includes('carry-on') && (
                      <Input
                        type="number"
                        value={formData.carry_on_weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, carry_on_weight: e.target.value }))}
                        placeholder={`Carry-on (${formData.weight_unit})`}
                        min="0"
                        step="0.1"
                        className="h-8 text-xs"
                      />
                    )}
                    {formData.luggage_type.includes('checked') && (
                      <Input
                        type="number"
                        value={formData.checked_weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, checked_weight: e.target.value }))}
                        placeholder={`Checked (${formData.weight_unit})`}
                        min="0"
                        step="0.1"
                        className="h-8 text-xs"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Trip Length Display */}
          {formData.start_date && formData.end_date && (
            <div className="bg-primary/10 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Trip Duration</p>
                  <p className="text-sm text-muted-foreground">We'll optimize your packing list for this length</p>
                </div>
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  {getTripLength()} day{getTripLength() !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
          )}

          {/* Remaining uses indicator for free tier */}
          {subscription.subscription_tier === 'economy' && (
            <div className="text-center text-sm text-muted-foreground mb-2">
              {getRemainingUses('packing_list')} of 3 daily uses remaining
            </div>
          )}

          </div>
          )}

          {/* Step navigation */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4) : s))}
              disabled={formStep === 1}
              className="min-w-[100px]"
            >
              Back
            </Button>

            {formStep < 4 ? (
              <Button
                type="button"
                onClick={() => {
                  if (formStep === 1 && !formData.destination) {
                    toast({ title: 'Pick a destination', description: 'Please choose where you are going to continue.' });
                    return;
                  }
                  if (formStep === 2 && (!formData.start_date || !formData.end_date)) {
                    toast({ title: 'Add your dates', description: 'Please select your travel dates to continue.' });
                    return;
                  }
                  if (formStep === 3 && !formData.trip_type) {
                    toast({ title: 'Pick a trip type', description: 'Please select your trip type to continue.' });
                    return;
                  }
                  setFormStep((s) => (s < 4 ? ((s + 1) as 1 | 2 | 3 | 4) : s));
                }}
                className="flex-1 sm:flex-initial sm:min-w-[180px] bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0 shadow-md shadow-cyan-500/20"
              >
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                className="flex-1 sm:flex-initial sm:min-w-[220px] bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0 shadow-md shadow-cyan-500/20"
                disabled={!formData.destination || !formData.start_date || !formData.end_date || !formData.trip_type || (subscription.subscription_tier === 'economy' && !canUseFeature('packing_list'))}
              >
                {(subscription.subscription_tier === 'economy' && !canUseFeature('packing_list'))
                  ? 'Daily Limit Reached'
                  : 'Generate My Packing List'}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>

    {/* Previous Lists Modal */}
    <Dialog open={showPreviousListsModal} onOpenChange={setShowPreviousListsModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Load From Previous Trip</DialogTitle>
          <DialogDescription>
            Select a previous packing list to use as a template
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {previousLists.map(list => (
            <Card 
              key={list.id} 
              className="cursor-pointer hover:border-primary transition-colors" 
              onClick={() => handleLoadPreviousList(list)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="font-semibold">{list.title}</h3>
                    <p className="text-sm text-muted-foreground">{list.destination}</p>
                    <p className="text-xs text-muted-foreground">{list.dates}</p>
                  </div>
                  <Button size="sm" variant="ghost">
                    Load
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {previousLists.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Luggage className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No previous packing lists found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
}